import { useEffect, useRef, useState } from 'react';
import { Calendar } from 'lucide-react';

interface DateInputProps {
  id?: string;
  value?: string | null;
  onChange: (value: string) => void;
  placeholder?: string;
  required?: boolean;
  disabled?: boolean;
  className?: string;
  min?: string;
  max?: string;
}

export function DateInput({
  id,
  value,
  onChange,
  placeholder = 'JJ/MM/AAAA',
  required = false,
  disabled = false,
  className = 'input-field',
  min,
  max,
}: DateInputProps) {
  const inputRef = useRef<HTMLInputElement>(null);
  const datePickerRef = useRef<HTMLInputElement>(null);

  const toDisplayDate = (date?: string | null) => {
    if (!date) return '';

    const match = date.match(/^(\d{4})-(\d{2})-(\d{2})/);
    if (!match) return '';

    return `${match[3]}/${match[2]}/${match[1]}`;
  };

  const [displayValue, setDisplayValue] = useState(() =>
    toDisplayDate(value)
  );

  useEffect(() => {
    const nextValue = toDisplayDate(value);

    setDisplayValue((current) =>
      current === nextValue ? current : nextValue
    );
  }, [value]);

  const formatDigits = (digits: string) => {
    if (digits.length <= 2) {
      return digits;
    }

    if (digits.length <= 4) {
      return `${digits.slice(0, 2)}/${digits.slice(2)}`;
    }

    return `${digits.slice(0, 2)}/${digits.slice(2, 4)}/${digits.slice(4)}`;
  };

  /**
   * Retrouve la position du curseur après avoir ajouté les "/".
   *
   * Exemple :
   *   12092026
   *   12/09/2026
   *
   * On se base sur le nombre de chiffres situés avant le curseur,
   * pas sur la position absolue.
   */
  const getCursorPosition = (
    formattedValue: string,
    digitCount: number
  ) => {
    if (digitCount <= 0) {
      return 0;
    }

    let digitsSeen = 0;

    for (let i = 0; i < formattedValue.length; i++) {
      if (/\d/.test(formattedValue[i])) {
        digitsSeen++;

        if (digitsSeen === digitCount) {
          return i + 1;
        }
      }
    }

    return formattedValue.length;
  };

  const handleTextChange = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const inputValue = event.target.value;
    const cursorPosition = event.target.selectionStart ?? inputValue.length;

    // Nombre de chiffres présents avant le curseur
    const digitsBeforeCursor = inputValue
      .slice(0, cursorPosition)
      .replace(/\D/g, '').length;

    const digits = inputValue.replace(/\D/g, '').slice(0, 8);

    const formatted = formatDigits(digits);

    setDisplayValue(formatted);

    // Restaurer le curseur une fois React ayant mis à jour l'input
    requestAnimationFrame(() => {
      const input = inputRef.current;

      if (!input) return;

      const newCursorPosition = getCursorPosition(
        formatted,
        digitsBeforeCursor
      );

      input.setSelectionRange(
        newCursorPosition,
        newCursorPosition
      );
    });

    if (digits.length === 0) {
      onChange('');
      return;
    }

    if (digits.length !== 8) {
      return;
    }

    const day = Number(digits.slice(0, 2));
    const month = Number(digits.slice(2, 4));
    const year = Number(digits.slice(4, 8));

    const date = new Date(year, month - 1, day);

    const valid =
      date.getFullYear() === year &&
      date.getMonth() === month - 1 &&
      date.getDate() === day;

    if (!valid) {
      return;
    }

    onChange(
      `${String(year).padStart(4, '0')}-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}`
    );
  };

  const openCalendar = () => {
    const picker = datePickerRef.current;

    if (!picker) return;

    if (typeof picker.showPicker === 'function') {
      picker.showPicker();
    } else {
      picker.click();
    }
  };

  return (
    <div className="relative">
      <input
        ref={inputRef}
        type="text"
        inputMode="numeric"
        placeholder={placeholder}
        value={displayValue}
        onChange={handleTextChange}
        className={`${className} pr-11`}
        maxLength={10}
        required={required}
        disabled={disabled}
      />

      <button
        type="button"
        onClick={openCalendar}
        disabled={disabled}
        className="
          absolute right-0 top-0
          h-full px-3
          flex items-center justify-center
          text-gray-500 hover:text-primary-600
          disabled:opacity-40 disabled:cursor-not-allowed
        "
        aria-label="Ouvrir le calendrier"
      >
        <Calendar className="w-5 h-5" />
      </button>

      <input
        id={id}
        ref={datePickerRef}
        type="date"
        value={value?.slice(0, 10) || ''}
        min={min}
        max={max}
        onChange={(event) => {
          const newValue = event.target.value;

          onChange(newValue);
          setDisplayValue(toDisplayDate(newValue));
        }}
        disabled={disabled}
        className="absolute opacity-0 pointer-events-none"
        tabIndex={-1}
        aria-hidden="true"
      />
    </div>
  );
}